import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, fileToBase64, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, ArrowLeft, Upload, CreditCard, Building2, Users,
  Image as ImageIcon, FileText, Check, Timer, ShieldAlert, MapPin,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Supplier Onboarding — Phase 1 · 5 simple steps.
 * White-glove: no artwork uploads (Display America / T+L design every station).
 */
const STEPS = [
  { id: "company",     label: "Company Information", icon: Building2 },
  { id: "contact",     label: "Primary Contact",     icon: Users },
  { id: "package",     label: "Supplier Package",    icon: CreditCard },
  { id: "assets",      label: "Brand Assets",        icon: ImageIcon },
  { id: "capability",  label: "Company Capabilities",icon: FileText },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const { user, loading } = useAuth();

  // Prime Supplier registration is CLOSED to the public as of Feb 2026.
  // Everyone reaching /onboarding sees the closed screen unless an
  // authenticated admin explicitly requests the manual-create flow via
  // `?admin=1`. Existing Primes never hit this page — they land on their
  // dashboard directly after login.
  const isAdminOverride =
    !loading && user?.role === "admin" && search.get("admin") === "1";
  if (!loading && !isAdminOverride) {
    return <PrimeRegistrationClosed nav={nav} />;
  }
  return <OnboardingInner nav={nav} search={search} user={user} loading={loading} />;
}

function PrimeRegistrationClosed({ nav }) {
  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-16 md:py-24 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60 mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-[#A39171] inline-block" />
            Registration Notice
          </div>
          <h1 className="serif text-4xl sm:text-6xl leading-tight" data-testid="prime-closed-title">
            Prime Supplier Registration Closed
          </h1>
          <p className="text-white/80 mt-5 max-w-2xl text-lg leading-relaxed">
            Prime Supplier registration for the Travel + Leisure Strategic
            Supplier Summit is now closed. Thank you for your interest in
            partnering with Travel + Leisure.
          </p>
        </div>
      </section>

      <section className="container-summit py-12 md:py-16 max-w-3xl">
        <div className="border border-[#A39171]/50 bg-[#FBF9F5] p-6 md:p-8" data-testid="prime-closed-sbe-cta">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#A39171]">Are You an SBE?</div>
          <h2 className="serif text-2xl md:text-3xl mt-3">Small Business Enterprises are still welcome.</h2>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            If you are a Small Business Enterprise (SBE) interested in
            participating in the Summit and Business Matchmaking with
            participating Prime Suppliers, please use the SBE registration
            below.
          </p>
          <button
            onClick={() => nav("/register/sbe")}
            data-testid="prime-closed-sbe-btn"
            className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-medium bg-black text-white hover:bg-zinc-800 px-5 py-3 border border-black">
            Register as an SBE <ArrowRight size={14} />
          </button>
        </div>

        <div className="mt-8 border border-zinc-200 p-6 md:p-7" data-testid="prime-closed-existing">
          <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Existing Prime Suppliers</div>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            If you are an invited Prime Supplier and need assistance with
            an existing registration — adding a representative, reviewing
            proofs, updating billing, or accessing your dashboard — please
            contact the Display America Summit team at{" "}
            <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">
              info@displayamerica.com
            </a>.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={() => nav("/login")}
              className="text-[11px] uppercase tracking-[0.22em] border border-black hover:bg-black hover:text-white px-4 py-2"
              data-testid="prime-closed-signin">
              Sign In to Existing Account
            </button>
            <button
              onClick={() => nav("/")}
              className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black px-4 py-2">
              Back to Summit Home
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function OnboardingInner({ nav, search, user, loading }) {
  const [event, setEvent] = useState(null);
  const [exhibitor, setExhibitor] = useState(null);
  const [loadingE, setLoadingE] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    company_name: "", description: "", industry_category: "",
    primary_contact: "", contact_title: "", email: "", phone: "",
    website: "", products_services: "",
    billing_address_line1: "", billing_address_line2: "",
    billing_city: "", billing_state: "", billing_postal_code: "",
    billing_country: "US",
  });

  // Industry categories seed the Step-1 dropdown. Load once on mount.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/industry-categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => { api.get("/event").then(({ data }) => setEvent(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { setLoadingE(false); return; }
    api.get("/exhibitors/me").then(({ data }) => {
      setExhibitor(data.exhibitor);
      setForm((f) => ({ ...f,
        company_name: data.exhibitor.company_name || "",
        description: data.exhibitor.description || "",
        industry_category: data.exhibitor.industry_category || "",
        primary_contact: data.exhibitor.primary_contact || "",
        contact_title: data.exhibitor.contact_title || "",
        email: data.exhibitor.email || user.email || "",
        phone: data.exhibitor.phone || "",
        website: data.exhibitor.website || "",
        products_services: data.exhibitor.products_services || "",
        billing_address_line1: data.exhibitor.billing_address_line1 || "",
        billing_address_line2: data.exhibitor.billing_address_line2 || "",
        billing_city: data.exhibitor.billing_city || "",
        billing_state: data.exhibitor.billing_state || "",
        billing_postal_code: data.exhibitor.billing_postal_code || "",
        billing_country: data.exhibitor.billing_country || "US",
      }));
      const idx = firstUnfinishedStep(data.exhibitor);
      if (idx != null) setStepIdx(idx);
    }).catch(() => setExhibitor(null)).finally(() => setLoadingE(false));
  }, [user, loading]);

  // Legacy Stripe redirect param is preserved for compatibility but no longer
  // used — online checkout is disabled and DA Accounting invoices manually.
  useEffect(() => {
    if (search.get("paid") === "1") {
      setStepIdx(3);
    }
  }, [search]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const step = STEPS[stepIdx];
  const goNext = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const goPrev = () => setStepIdx((i) => Math.max(i - 1, 0));
  const progressPct = ((stepIdx + 1) / STEPS.length) * 100;

  if (loading || loadingE) return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-12 md:py-16">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/60">
            <span className="h-px w-8 bg-[#A39171]" />Prime Supplier Registration
          </div>
          <h1 className="serif text-4xl sm:text-6xl mt-4 leading-tight">
            Complete your supplier registration.
          </h1>
          <p className="mt-5 max-w-2xl text-white/75 leading-relaxed">
            Choose your station. Upload your logo. Secure your participation.
            <br className="hidden sm:block" />
            <span className="text-white/50">Approximately 5 minutes.</span>
          </p>

          <div className="mt-10">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/60">
              <span data-testid="onboarding-step-label">Step {stepIdx + 1} of {STEPS.length} · {step.label}</span>
              <span className="tabular-nums">{Math.round(progressPct)}%</span>
            </div>
            <div className="mt-3 h-1 bg-white/10">
              <motion.div initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-[#A39171]" />
            </div>
            <div className="mt-5 grid grid-cols-5 gap-x-2 gap-y-3">
              {STEPS.map((s, i) => {
                const done = i < stepIdx || isStepComplete(s.id, exhibitor);
                const active = i === stepIdx;
                return (
                  <button key={s.id} onClick={() => setStepIdx(i)}
                    data-testid={`onboarding-dot-${s.id}`}
                    className={`text-left group ${active ? "opacity-100" : "opacity-70 hover:opacity-100"}`}>
                    <div className={`h-1 mb-2 ${active ? "bg-[#A39171]" : done ? "bg-emerald-500" : "bg-white/15"}`} />
                    <div className={`text-[10px] uppercase tracking-[0.14em] ${active ? "text-white" : "text-white/60"}`}>
                      {s.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="container-summit py-12 md:py-16">
        <AnimatePresence mode="wait">
          <motion.div key={step.id}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={step.id === "package" ? "max-w-5xl mx-auto" : "max-w-2xl mx-auto"}>
            {step.id === "company"    && <StepCompany form={form} set={set} exhibitor={exhibitor} categories={categories} saving={saving} setSaving={setSaving} onSaved={(e) => { setExhibitor(e); goNext(); }} />}
            {step.id === "contact"    && <StepContact form={form} set={set} exhibitor={exhibitor} saving={saving} setSaving={setSaving} onSaved={(e) => { setExhibitor(e); goNext(); }} onBack={goPrev} />}
            {step.id === "package"    && <StepPackage event={event} exhibitor={exhibitor} onBack={goPrev} onSkip={goNext} />}
            {step.id === "assets"     && <StepAssets exhibitor={exhibitor} setExhibitor={setExhibitor} onNext={goNext} onBack={goPrev} />}
            {step.id === "capability" && <StepCapability form={form} set={set} exhibitor={exhibitor} setExhibitor={setExhibitor} onFinish={() => nav("/registration-complete")} onBack={goPrev} />}
          </motion.div>
        </AnimatePresence>
      </section>
    </Layout>
  );
}

/* ============================================================
   STEP 1 · COMPANY INFORMATION
   No backend call yet — we need email (Step 2) before we can POST /register.
   ============================================================ */
function StepCompany({ form, set, exhibitor, categories, saving, setSaving, onSaved }) {
  const descCount = (form.description || "").length;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Company name is required.");
    if (!form.industry_category) return toast.error("Please select your industry category.");
    if (!form.description.trim()) return toast.error("A short company description is required.");
    if (descCount > 1500) return toast.error("Company description must be 1500 characters or fewer.");
    // If we already have an exhibitor (returning session), PUT the updated fields.
    if (exhibitor) {
      setSaving(true);
      try {
        const { data } = await api.put("/exhibitors/me", {
          company_name: form.company_name.trim(),
          description: form.description,
          industry_category: form.industry_category,
        });
        onSaved(data.exhibitor);
      } catch (err) {
        const raw = err?.response?.data?.detail;
        const msg = typeof raw === "string" ? raw
                  : Array.isArray(raw) && raw[0]?.msg ? raw[0].msg
                  : "Could not save.";
        toast.error(msg);
      } finally { setSaving(false); }
      return;
    }
    onSaved(null);
  };
  return (
    <form onSubmit={submit} className="grid gap-6">
      <div className="overline"><span className="accent-rule" />Step 1 of 5</div>
      <h2 className="serif text-4xl">Company information.</h2>
      <p className="text-zinc-600">These fields populate your public Supplier Profile.</p>

      <div>
        <Label className="overline">Company Name *</Label>
        <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
          required data-testid="ob-company" className="h-12 rounded-none mt-2" />
      </div>

      <div>
        <Label className="overline">Industry Category *</Label>
        <select value={form.industry_category} onChange={(e) => set("industry_category", e.target.value)}
          required data-testid="ob-industry-category"
          className="w-full h-12 mt-2 border border-zinc-300 px-3 text-sm bg-white outline-none focus:border-black">
          <option value="">Select your industry category…</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        <p className="text-xs text-zinc-500 mt-2">
          Determines which Supplier Station block you can reserve on the Showcase floor.
        </p>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <Label className="overline">Company Description *</Label>
          <span className={`text-[10px] uppercase tracking-[0.18em] ${descCount > 1500 ? "text-red-600" : "text-zinc-500"}`}
                data-testid="ob-description-count">
            {descCount} / 1500
          </span>
        </div>
        <Textarea rows={5} value={form.description}
          onChange={(e) => set("description", e.target.value.slice(0, 1500))}
          maxLength={1500}
          placeholder="Tell us what your company does — one paragraph is plenty. Displayed on your public Supplier Profile."
          data-testid="ob-description" className="rounded-none mt-2" required />
      </div>

      <div className="flex justify-end pt-2">
        <button disabled={saving} className="btn-black h-12 px-8" data-testid="ob-continue">
          {saving ? "Saving…" : "Continue"} <ArrowRight size={14} />
        </button>
      </div>
    </form>
  );
}

/* ============================================================
   STEP 2 · PRIMARY CONTACT
   ============================================================ */
function StepContact({ form, set, exhibitor, saving, setSaving, onSaved, onBack }) {
  const { refresh: refreshAuth } = useAuth();
  const isNewRegistration = !exhibitor;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const validateUrl = (v) => {
    if (!v) return false;
    // Accept "example.com", "www.x.com", or full URLs. Reject spaces + invalid TLDs.
    return /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*)?$/i.test(v.trim());
  };
  const normalizeUrl = (v) => {
    const t = (v || "").trim();
    if (!t) return "";
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.primary_contact.trim() || !form.email.trim()) return toast.error("Name and email are required.");
    if (!form.contact_title.trim()) return toast.error("Primary Contact Title is required.");
    if (!validateUrl(form.website)) return toast.error("Please enter a valid company website.");
    // Billing address is required so DA Accounting can invoice via QuickBooks Online.
    if (!form.billing_address_line1?.trim()) return toast.error("Billing Address Line 1 is required.");
    if (!form.billing_city?.trim())          return toast.error("Billing City is required.");
    if (!form.billing_state?.trim())         return toast.error("Billing State / Province is required.");
    if (!form.billing_postal_code?.trim())   return toast.error("Billing ZIP / Postal Code is required.");
    if (!form.billing_country?.trim())       return toast.error("Billing Country is required.");
    // Terms & Conditions acknowledgement is required for NEW registrations only.
    // Existing suppliers who registered before this policy was introduced are
    // not retroactively re-prompted.
    if (isNewRegistration && !termsAccepted) {
      return toast.error("Please review and acknowledge the Prime Supplier Terms & Conditions to continue.");
    }
    setSaving(true);
    try {
      let ex = exhibitor;
      if (!ex) {
        const payload = {
          company_name: form.company_name.trim(),
          primary_contact: form.primary_contact.trim(),
          contact_title: form.contact_title.trim(),
          email: form.email.trim(),
          phone: form.phone || "",
          website: normalizeUrl(form.website),
          description: form.description || "",
          industry_category: form.industry_category || null,
          billing_address_line1: form.billing_address_line1.trim(),
          billing_address_line2: (form.billing_address_line2 || "").trim(),
          billing_city: form.billing_city.trim(),
          billing_state: form.billing_state.trim(),
          billing_postal_code: form.billing_postal_code.trim(),
          billing_country: form.billing_country.trim(),
          terms_accepted: true,
          terms_version: "prime-supplier-terms-v1",
        };
        const { data } = await api.post("/exhibitors/register", payload);
        ex = data.exhibitor;
        if (data.magic_link_token) {
          try {
            const { data: verified } = await api.get(`/auth/verify?token=${data.magic_link_token}`);
            if (verified?.token) {
              setToken(verified.token);
              await refreshAuth();
            }
          } catch { /* non-fatal — the supplier can still sign in via the magic link email */ }
        }
      } else {
        const { data } = await api.put("/exhibitors/me", {
          primary_contact: form.primary_contact.trim(),
          contact_title: form.contact_title.trim(),
          phone: form.phone || null,
          website: normalizeUrl(form.website),
          billing_address_line1: form.billing_address_line1.trim(),
          billing_address_line2: (form.billing_address_line2 || "").trim(),
          billing_city: form.billing_city.trim(),
          billing_state: form.billing_state.trim(),
          billing_postal_code: form.billing_postal_code.trim(),
          billing_country: form.billing_country.trim(),
        });
        ex = data.exhibitor;
      }
      onSaved(ex);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw
                : Array.isArray(raw) && raw[0]?.msg ? raw[0].msg
                : "Could not save.";
      toast.error(msg);
    }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-6">
      <div className="overline"><span className="accent-rule" />Step 2 of 5</div>
      <h2 className="serif text-4xl">Primary contact.</h2>
      <p className="text-zinc-600">The person Display America will coordinate with.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Full Name *</Label>
          <Input required value={form.primary_contact} onChange={(e) => set("primary_contact", e.target.value)}
            data-testid="ob-contact" className="h-12 rounded-none mt-2" />
        </div>
        <div>
          <Label className="overline">Title *</Label>
          <Input required value={form.contact_title} onChange={(e) => set("contact_title", e.target.value)}
            placeholder="e.g. Director of Procurement"
            data-testid="ob-title" className="h-12 rounded-none mt-2" />
        </div>
        <div>
          <Label className="overline">Email *</Label>
          <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
            data-testid="ob-email" className="h-12 rounded-none mt-2" />
        </div>
        <div>
          <Label className="overline">Phone</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
            data-testid="ob-phone" className="h-12 rounded-none mt-2" />
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Company Website *</Label>
          <Input required value={form.website} onChange={(e) => set("website", e.target.value)}
            placeholder="www.yourcompany.com"
            data-testid="ob-website" className="h-12 rounded-none mt-2" />
          <p className="text-xs text-zinc-500 mt-2">Displayed publicly on your Supplier Profile.</p>
        </div>
      </div>

      <div className="mt-6 border border-zinc-200 bg-zinc-50/60 p-5 md:p-6">
        <div className="overline"><span className="accent-rule" />Billing Address</div>
        <h3 className="serif text-2xl mt-2">For your Display America invoice.</h3>
        <p className="text-xs text-zinc-500 mt-1 max-w-lg">
          Display America Accounting will attach these details to the $2,500 Supplier
          Registration Fee invoice they send you.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div className="sm:col-span-2">
            <Label className="overline">Address Line 1 *</Label>
            <Input required value={form.billing_address_line1 || ""} onChange={(e) => set("billing_address_line1", e.target.value)}
              placeholder="Street address, PO Box, or company name"
              data-testid="ob-billing-line1" className="h-12 rounded-none mt-2" />
          </div>
          <div className="sm:col-span-2">
            <Label className="overline">Address Line 2</Label>
            <Input value={form.billing_address_line2 || ""} onChange={(e) => set("billing_address_line2", e.target.value)}
              placeholder="Suite, unit, building, floor (optional)"
              data-testid="ob-billing-line2" className="h-12 rounded-none mt-2" />
          </div>
          <div>
            <Label className="overline">City *</Label>
            <Input required value={form.billing_city || ""} onChange={(e) => set("billing_city", e.target.value)}
              data-testid="ob-billing-city" className="h-12 rounded-none mt-2" />
          </div>
          <div>
            <Label className="overline">State / Province *</Label>
            <Input required value={form.billing_state || ""} onChange={(e) => set("billing_state", e.target.value)}
              placeholder="e.g. FL"
              data-testid="ob-billing-state" className="h-12 rounded-none mt-2" />
          </div>
          <div>
            <Label className="overline">ZIP / Postal Code *</Label>
            <Input required value={form.billing_postal_code || ""} onChange={(e) => set("billing_postal_code", e.target.value)}
              data-testid="ob-billing-postal" className="h-12 rounded-none mt-2" />
          </div>
          <div>
            <Label className="overline">Country *</Label>
            <Input required value={form.billing_country || "US"} onChange={(e) => set("billing_country", e.target.value)}
              data-testid="ob-billing-country" className="h-12 rounded-none mt-2" />
          </div>
        </div>
      </div>

      {isNewRegistration && (
        <TermsAcknowledgement accepted={termsAccepted} onChange={setTermsAccepted} />
      )}

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="text-xs uppercase tracking-[0.18em] px-4 h-12 border border-zinc-300">
          <ArrowLeft size={12} className="inline mr-2" /> Back
        </button>
        <button disabled={saving || (isNewRegistration && !termsAccepted)} className="btn-black h-12 px-8 disabled:opacity-40" data-testid="ob-continue">
          {saving ? "Saving…" : "Continue"} <ArrowRight size={14} />
        </button>
      </div>
    </form>
  );
}

/* Reusable T&C acknowledgement block — only shown on new registrations. */
function TermsAcknowledgement({ accepted, onChange }) {
  return (
    <div className="mt-6 border border-zinc-200 bg-zinc-50/60 p-5" data-testid="ob-terms-block">
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={accepted}
               onChange={(e) => onChange(e.target.checked)}
               data-testid="ob-terms-checkbox"
               className="mt-1 h-4 w-4 border-zinc-400 accent-black cursor-pointer shrink-0" />
        <span className="text-sm text-zinc-800 leading-relaxed">
          I have read and agree to the{" "}
          <a href="/documents/prime-supplier-terms" target="_blank" rel="noreferrer"
             className="underline underline-offset-4 hover:text-black text-[#A39171]"
             data-testid="ob-terms-link">
            Prime Supplier Terms &amp; Conditions
          </a>
          , including the $2,500 participation fee, the 48-hour cancellation
          window, and the production and payment deadlines (August 4, 2026 ·
          August 10, 2026 · September 7, 2026).
        </span>
      </label>
    </div>
  );
}

/* ============================================================
   STEP 3 · SUPPLIER PACKAGE (was "Payment")
   ============================================================ */
const PACKAGE_ITEMS = [
  "Branded Supplier Station",
  "Illuminated Lightbox",
  "Custom Counter Graphics",
  "Business Matchmaking",
  "Educational Sessions",
  "Sourcing Fireside Chat",
  "Networking Lunch",
];

function StepPackage({ event, exhibitor, onBack, onSkip }) {
  const [busy, setBusy] = useState(false);
  const paid = exhibitor?.payment_status === "paid";
  const [stations, setStations] = useState([]);
  const [held, setHeld] = useState(null);         // {station, hold_expires_at}
  const [remaining, setRemaining] = useState(0);  // seconds
  const [warnOpen, setWarnOpen] = useState(false);
  const warnedRef = useRef(false);

  // Load stations + any existing hold this supplier already has
  const refreshStations = async () => {
    try {
      const { data } = await api.get("/stations");
      setStations(data.stations || []);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { refreshStations(); }, []);

  useEffect(() => {
    if (!exhibitor || paid) return;
    api.get("/exhibitors/me/station").then(({ data }) => {
      if (data.station && (data.station.status === "reserved" || data.station.status === "held")) {
        setHeld({ station: data.station, hold_expires_at: data.station.hold_expires_at || null });
      }
    }).catch(() => {});
  }, [exhibitor, paid]);

  // Countdown ticker retained only for legacy 15-minute holds (pre-Feb-2026).
  // Under the QuickBooks manual-invoicing flow, hold_expires_at is null and
  // this effect is a no-op.
  useEffect(() => {
    if (!held || !held.hold_expires_at) { setRemaining(0); return; }
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(held.hold_expires_at).getTime() - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 120 && secs > 0 && !warnedRef.current) {
        warnedRef.current = true;
        setWarnOpen(true);
      }
      if (secs === 0) {
        toast.error("Your station hold expired. Please select a station again.");
        setHeld(null);
        warnedRef.current = false;
        refreshStations();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [held]);

  const holdStation = async (stationId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stations/${stationId}/hold`);
      setHeld({ station: data.station, hold_expires_at: data.hold_expires_at });
      warnedRef.current = false;
      await refreshStations();
      toast.success(`Station ${data.station.label} reserved. Display America will email your invoice shortly.`);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not reserve that station.");
    } finally { setBusy(false); }
  };

  const extendHold = async () => {
    if (!held) return;
    await holdStation(held.station.id);
    setWarnOpen(false);
  };

  const releaseHold = async () => {
    if (!held) return;
    try {
      await api.post(`/stations/${held.station.id}/release`);
      toast.success("Station released.");
    } catch { /* non-fatal — the poller will reconcile */ }
    setHeld(null);
    warnedRef.current = false;
    setWarnOpen(false);
    await refreshStations();
  };

  // QuickBooks manual-invoicing (Feb 2026): station is reserved indefinitely
  // when picked. There is no online payment step at registration — Display
  // America emails the invoice out-of-band. This CTA advances the supplier
  // to the next onboarding step (Brand Assets).
  const advanceAfterReservation = () => {
    if (!held) return toast.error("Please choose your Supplier Station before continuing.");
    onSkip();
  };

  const available = useMemo(() => {
    return (stations || []).filter((s) => {
      const status = s.status || (s.exhibitor_id ? "approved" : "available");
      if (status === "approved") return false;
      if ((status === "reserved" || status === "held") && s.held_by && held?.station?.id !== s.id) return false;
      return true;
    });
  }, [stations, held]);

  return (
    <div className="grid gap-6">
      <div className="overline"><span className="accent-rule" />Step 3 of 5</div>
      <h2 className="serif text-4xl">Supplier package.</h2>
      <p className="text-zinc-600">One inclusive package, designed for serious supplier partners.</p>

      <div className="border border-black bg-white" data-testid="package-card">
        {/* Price header */}
        <div className="p-6 md:p-8 border-b border-zinc-200 flex items-baseline gap-3">
          <span className="serif text-5xl tabular-nums">$2,500</span>
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500">Per Prime Supplier</span>
        </div>

        {!paid && (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-0" data-testid="station-picker">
            {/* LEFT — Station picker */}
            <div className="p-6 md:p-8">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <div className="overline">Choose Your Supplier Station</div>
                  <div className="serif text-3xl mt-2" data-testid="station-headline">Choose your station.</div>
                  <p className="text-sm text-zinc-500 mt-3 max-w-lg leading-relaxed">
                    Choose any available supplier station. Once you select a station, it is reserved
                    for your company and Display America will email your invoice shortly.
                    Your station stays reserved until payment is received and your registration
                    is confirmed.
                  </p>
                </div>
                {held && (
                  <div className="text-right" data-testid="reservation-badge">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 inline-flex items-center gap-1">
                      <Check size={12} strokeWidth={2.5} /> Reserved
                    </div>
                    <div className="serif text-lg mt-1">Station {held.station.label}</div>
                    <button onClick={releaseHold} className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-black mt-1" data-testid="release-hold-inline">
                      Change station
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="station-grid">
                {stations.map((s) => {
                  const status = s.status || (s.exhibitor_id ? "approved" : "available");
                  const isSelected = held?.station?.id === s.id;
                  const takenByAnother = (status === "reserved" || status === "held") && !isSelected;
                  const disabled = (status === "approved") || takenByAnother;
                  const stateLabel = status === "approved" ? "Assigned"
                    : isSelected ? "Selected"
                    : takenByAnother ? "Reserved"
                    : "Available";
                  const tileCls = isSelected
                    ? "bg-[#A39171] text-white border-[#A39171] shadow-[0_4px_0_#8b7a5f] -translate-y-0.5 scale-[1.02]"
                    : disabled
                    ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                    : "bg-white text-black border-zinc-300 hover:border-black hover:-translate-y-0.5";
                  return (
                    <button key={s.id}
                      type="button"
                      onClick={() => !disabled && !busy && holdStation(s.id)}
                      disabled={disabled || busy}
                      aria-label={`Station ${s.label} — ${stateLabel}`}
                      data-testid={`station-pick-${s.number}`}
                      className={`relative border py-4 px-2 text-center transition-all duration-200 ${tileCls} rounded-sm min-w-0`}>
                      {isSelected && (
                        <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-[#A39171]" aria-hidden="true">
                          <Check size={10} strokeWidth={3} />
                        </span>
                      )}
                      <div className="text-[10px] uppercase tracking-[0.12em] opacity-70 leading-none">Station</div>
                      <div className="serif text-2xl tabular-nums leading-none mt-1.5">{s.label}</div>
                      <div className={`text-[10px] uppercase tracking-[0.08em] mt-2 leading-none ${isSelected ? "text-white/90" : disabled ? "text-zinc-400" : "text-zinc-500"}`}>
                        {stateLabel}
                      </div>
                    </button>
                  );
                })}
                {stations.length === 0 && (
                  <div className="col-span-full text-sm text-zinc-500">Loading stations…</div>
                )}
              </div>

              <p className="text-xs text-zinc-500 mt-5 tabular-nums flex items-center gap-2" data-testid="station-availability">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
                Available <span className="text-black">{available.length}</span>
                <span className="text-zinc-300">·</span>
                On hold or reserved <span className="text-black">{stations.length - available.length}</span>
              </p>
            </div>

            {/* RIGHT — Station preview panel */}
            <aside className="bg-zinc-50 border-t lg:border-t-0 lg:border-l border-zinc-200 p-6 md:p-8" data-testid="station-preview-panel">
              <div className="overline mb-4">Your Supplier Package Includes</div>
              <ul className="grid grid-cols-1 gap-2.5">
                {PACKAGE_ITEMS.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm">
                    <Check size={14} className="text-[#A39171] mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-6 border-t border-zinc-200">
                <div className="overline mb-4">Station Preview</div>
                <div className="relative aspect-[4/5] bg-white overflow-hidden border border-zinc-200" data-testid="station-preview-image">
                  <img
                    src="/brand/station-preview.png"
                    alt="Rendering of a Travel + Leisure branded supplier station — illuminated lightbox with co-branded logos above a modular black counter"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="text-[11px] leading-relaxed text-zinc-500 mt-3">
                  Actual graphics will be customized using your approved company branding.
                </p>
              </div>
            </aside>
          </div>
        )}

        <div className="border-t border-zinc-200 p-6 bg-zinc-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-xs text-zinc-500">
            {paid ? "Payment received — you can proceed to Brand Assets."
                  : held ? (
                      <span data-testid="selected-station-label">
                        Selected: <span className="text-black font-medium">Station {held.station.label}</span> · Display America will email your invoice.
                      </span>
                    )
                  : "Select a station to continue."}
          </div>
          {paid ? (
            <button onClick={onSkip} className="btn-black h-12 px-6" data-testid="ob-continue">Continue <ArrowRight size={14} /></button>
          ) : (
            <button onClick={advanceAfterReservation} disabled={busy || !held} className="btn-black h-12 px-6 disabled:opacity-40" data-testid="ob-pay">
              {busy ? "Working…" : held ? "Continue to Brand Assets" : "Select a station to continue"} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-start pt-2">
        <button onClick={onBack} className="text-xs uppercase tracking-[0.18em] px-4 h-12 border border-zinc-300">
          <ArrowLeft size={12} className="inline mr-2" /> Back
        </button>
      </div>

      {/* Warning modal — appears once at 2 minutes remaining */}
      <AnimatePresence>
        {warnOpen && held && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="hold-warning-modal">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white max-w-md w-full">
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <ShieldAlert size={18} className="text-red-600" strokeWidth={1.4} />
                  <div className="overline">Station Hold Expiring</div>
                </div>
                <h3 className="serif text-2xl mt-3">Only {formatMMSS(remaining)} remaining.</h3>
                <p className="text-sm text-zinc-600 mt-3">
                  Station <span className="text-black">{held.station.label}</span> will be released back to available inventory unless you extend the hold or continue with registration.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button onClick={extendHold} className="btn-black h-11 px-6 flex-1" data-testid="hold-warning-extend">
                    Extend Hold <Timer size={14} />
                  </button>
                  <button onClick={releaseHold} className="h-11 px-6 border border-zinc-300 text-xs uppercase tracking-[0.18em]" data-testid="hold-warning-release">
                    Release Station
                  </button>
                </div>
                <button onClick={() => setWarnOpen(false)} className="text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-black mt-5" data-testid="hold-warning-close">
                  Dismiss
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatMMSS(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/* ============================================================
   STEP 4 · BRAND ASSETS (simplified — logo + brand guidelines only)
   ============================================================ */
function StepAssets({ exhibitor, setExhibitor, onNext, onBack }) {
  const [busyLogo, setBusyLogo] = useState(false);
  const [busyBrand, setBusyBrand] = useState(false);

  const upload = async (kind, file, setBusy) => {
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await fileToBase64(file);
      const { data: up } = await api.post("/uploads", {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data_base64: base64,
        purpose: kind === "logo" ? "logo" : "brand_guidelines",
      });
      const fieldKey = kind === "logo" ? "logo_file_id" : "brand_guidelines_file_id";
      const { data } = await api.put("/exhibitors/me", { [fieldKey]: up.file_id });
      setExhibitor(data.exhibitor);
      toast.success(`${kind === "logo" ? "Logo" : "Brand guidelines"} uploaded.`);
    } catch (e) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6">
      <div className="overline"><span className="accent-rule" />Step 4 of 5</div>
      <h2 className="serif text-4xl">Brand assets.</h2>
      <p className="text-zinc-600">
        Just your logo. Display America and Travel + Leisure design every supplier station on your behalf —
        no artwork templates or booth files required.
      </p>

      <div className="border border-zinc-200 p-6">
        <div className="overline mb-3">Company Logo *</div>
        <label className="btn-outline-black h-11 text-xs cursor-pointer inline-flex" data-testid="ob-logo-label">
          <Upload size={14} /> {exhibitor?.logo_file_id ? "Replace logo" : "Upload logo"} (SVG, EPS, PDF, PNG, JPG)
          <input type="file" hidden accept="image/*,application/pdf,application/postscript,.eps,.ai" data-testid="ob-logo"
            onChange={(e) => upload("logo", e.target.files?.[0], setBusyLogo)} />
        </label>
        <div className="text-xs text-zinc-500 mt-3">
          {busyLogo ? "Uploading…" : exhibitor?.logo_file_id ? "Logo received." : "Required to continue. High-resolution vector formats preferred (SVG, EPS, or PDF). Raster files must be 125 DPI or higher."}
        </div>
      </div>

      <div className="border border-zinc-200 p-6">
        <div className="overline mb-3">Brand Guidelines PDF (optional)</div>
        <label className="btn-outline-black h-11 text-xs cursor-pointer inline-flex" data-testid="ob-brand-label">
          <Upload size={14} /> {exhibitor?.brand_guidelines_file_id ? "Replace guidelines" : "Upload guidelines"} (PDF)
          <input type="file" hidden accept="application/pdf" data-testid="ob-brand"
            onChange={(e) => upload("brand", e.target.files?.[0], setBusyBrand)} />
        </label>
        <div className="text-xs text-zinc-500 mt-3">
          {busyBrand ? "Uploading…" : exhibitor?.brand_guidelines_file_id ? "Brand guidelines received." : "Optional — colors, type, spacing, and voice guidance for your station."}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="text-xs uppercase tracking-[0.18em] px-4 h-12 border border-zinc-300">
          <ArrowLeft size={12} className="inline mr-2" /> Back
        </button>
        <button onClick={onNext} disabled={!exhibitor?.logo_file_id} className="btn-black h-12 px-8" data-testid="ob-continue">
          {exhibitor?.logo_file_id ? "Continue" : "Upload logo to continue"} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   STEP 5 · PRODUCTS & MATCHMAKING (final registration data)
   ============================================================ */
// Master vocabulary — the same list powers Products & Services AND
// Business Matchmaking Interests. Used for supplier profile display and
// the future matchmaking engine.
const CATEGORY_VOCAB = [
  "Construction", "Engineering", "Architecture", "Electrical", "Mechanical",
  "HVAC", "Security", "Risk Management", "IT", "Cybersecurity",
  "Staffing", "Marketing", "Furniture", "Printing", "Promotional Products",
  "Facilities Management", "Janitorial", "Landscaping", "Food Services",
  "Transportation", "Logistics", "Professional Services", "Consulting",
  "Financial Services", "Legal", "Other",
];

function StepCapability({ form, set, exhibitor, setExhibitor, onFinish, onBack }) {
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState(exhibitor?.business_areas || []);
  const [matchInterests, setMatchInterests] = useState(exhibitor?.matchmaking_preferences || []);

  const toggle = (list, setList, v) => setList((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);

  const finish = async () => {
    if (products.length === 0)      return toast.error("Please select at least one Products & Services category.");
    if (matchInterests.length === 0) return toast.error("Please select at least one Business Matchmaking Interest.");
    setSaving(true);
    try {
      await api.put("/exhibitors/me", {
        business_areas: products,
        matchmaking_preferences: matchInterests,
      });
      // Fire the "registration received — DA Accounting will invoice separately"
      // email + advance payment_status to `awaiting_invoice`. This is the final
      // server-side gate: if the billing address is incomplete the API returns
      // 400 and we must NOT tell the supplier they are done.
      try {
        await api.post("/exhibitors/me/complete-registration");
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : "We could not complete your registration. Please review Step 2 (billing address) and try again.";
        toast.error(msg);
        setSaving(false);
        return;
      }
      toast.success("Registration complete.");
      onFinish();
    } catch (e) { toast.error("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-8">
      <div className="overline"><span className="accent-rule" />Step 5 of 5</div>
      <h2 className="serif text-4xl">Products & matchmaking.</h2>
      <p className="text-zinc-600">
        These two selections power your public Supplier Profile and future Business Matchmaking.
      </p>

      {/* Products & Services multi-select */}
      <div data-testid="ob-products-block">
        <Label className="overline">What products and services does your organization provide? *</Label>
        <div className="text-xs text-zinc-500 mt-2">Select all that apply. Displayed on your public Supplier Profile.</div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="ob-products-services">
          {CATEGORY_VOCAB.map((v) => {
            const on = products.includes(v);
            return (
              <button key={v} type="button" onClick={() => toggle(products, setProducts, v)}
                data-testid={`ob-product-${slugify(v)}`}
                className={`px-3 py-2 text-xs border transition-colors ${on ? "bg-black text-white border-black" : "bg-white border-zinc-300 hover:border-black"}`}>
                {v}
              </button>
            );
          })}
        </div>
      </div>

      {/* Business Matchmaking Interests multi-select */}
      <div data-testid="ob-matchmaking-block">
        <Label className="overline">Which products or services are you interested in exploring during Business Matchmaking? *</Label>
        <div className="text-xs text-zinc-500 mt-2">Select all that apply. Used privately by the matchmaking engine to introduce you to relevant Small Business Enterprises.</div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="ob-matchmaking-interests">
          {CATEGORY_VOCAB.map((v) => {
            const on = matchInterests.includes(v);
            return (
              <button key={v} type="button" onClick={() => toggle(matchInterests, setMatchInterests, v)}
                data-testid={`ob-match-${slugify(v)}`}
                className={`px-3 py-2 text-xs border transition-colors ${on ? "bg-[#A39171] text-white border-[#A39171]" : "bg-white border-zinc-300 hover:border-[#A39171]"}`}>
                {v}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="text-xs uppercase tracking-[0.18em] px-4 h-12 border border-zinc-300">
          <ArrowLeft size={12} className="inline mr-2" /> Back
        </button>
        <button onClick={finish} disabled={saving} className="btn-black h-12 px-8" data-testid="ob-finish">
          <Check size={14} /> {saving ? "Finishing…" : "Finish Registration"}
        </button>
      </div>
    </div>
  );
}

function slugify(v) { return v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }

/* ============================================================
   HELPERS
   ============================================================ */
function isStepComplete(stepId, ex) {
  if (!ex) return false;
  switch (stepId) {
    case "company":    return !!(ex.company_name && ex.industry_category);
    case "contact":    return !!(ex.primary_contact && ex.email);
    // Under QuickBooks manual invoicing (Feb 2026) the "package" step is
    // considered complete once the supplier has reserved a station. The
    // payment reconciliation happens later, out-of-band, by Display America.
    case "package":    return !!ex.booth_number
                             || ["awaiting_invoice", "invoice_sent", "payment_received", "complete", "paid"]
                                  .includes(ex.payment_status || "");
    case "assets":     return !!ex.logo_file_id;
    case "capability": return !!(ex.capability_statement_file_id || ex.products_services);
    default: return false;
  }
}

function firstUnfinishedStep(exhibitor) {
  if (!exhibitor) return 0;
  for (let i = 0; i < STEPS.length; i++) {
    if (!isStepComplete(STEPS[i].id, exhibitor)) return i;
  }
  return STEPS.length - 1;
}
